import { Source } from "Constant/source";

import { getSourceIconByCode } from "helpers/Sources/getSourceIcon";

// Intentionally free of `SourceCreation.constants` imports: that module pulls
// in every `source_types/*` component.

/** Offline message source; not part of the `Source` enum. */
const MSG = "msg";

export const SourceCategory = Object.freeze({
    WEB: "Web",
    APP: "App",
    CRM: "CRM",
    OFFLINE: "Offline",
    OTHER: "Other",
});

/** The API source types carry no category / description; this fills them in. */
export const SOURCE_CATALOG_META = Object.freeze({
    [Source.js]: {
        category: SourceCategory.WEB,
        description:
            "Collect visitor interactions and events from your website.",
    },
    [Source.redirect]: {
        category: SourceCategory.WEB,
        description:
            "Track clicks on tracked links and redirect visitors to their destination.",
    },
    [Source.androidcapi]: {
        category: SourceCategory.APP,
        description: "Send events from your Android app to SignalSight.",
    },
    [Source.ioscapi]: {
        category: SourceCategory.APP,
        description: "Send events from your iOS app to SignalSight.",
    },
    [Source.appsflyer]: {
        category: SourceCategory.APP,
        description:
            "Connect AppsFlyer to measure web-to-app campaigns and installs.",
    },
    [Source.adjust]: {
        category: SourceCategory.APP,
        description:
            "Connect Adjust to measure web-to-app campaigns and installs.",
    },
    [Source.capi]: {
        category: SourceCategory.OFFLINE,
        description:
            "Bring events that happen outside your website into SignalSight.",
    },
    [MSG]: {
        category: SourceCategory.OFFLINE,
        description:
            "Capture conversations and events from your messaging channels.",
    },
    [Source.sftp]: {
        category: SourceCategory.OFFLINE,
        description: "Import files from your SFTP server.",
    },
    [Source.ftp]: {
        category: SourceCategory.OFFLINE,
        description: "Import files from your FTP server.",
    },
    [Source.upload]: {
        category: SourceCategory.OFFLINE,
        description: "Upload a file to import your source data.",
    },
    [Source.spreadsheet]: {
        category: SourceCategory.OFFLINE,
        description: "Use data from a Google spreadsheet as your source.",
    },
    [Source.bigquery]: {
        category: SourceCategory.OFFLINE,
        description: "Read source data from a Google BigQuery table.",
    },
    [Source.zoho]: {
        category: SourceCategory.CRM,
        description: "Connect Zoho CRM as a source for your customer data.",
    },
    [Source.hub]: {
        category: SourceCategory.CRM,
        description: "Connect HubSpot CRM as a source for your customer data.",
    },
    [Source.odoo]: {
        category: SourceCategory.CRM,
        description: "Connect Odoo CRM as a source for your customer data.",
    },
    [Source.salesforce]: {
        category: SourceCategory.CRM,
        description: "Connect Salesforce as a source for your customer data.",
    },
    [Source.freshsales]: {
        category: SourceCategory.CRM,
        description: "Connect Freshsales as a source for your customer data.",
    },
    [Source.dynamics]: {
        category: SourceCategory.CRM,
        description:
            "Connect Microsoft Dynamics 365 as a source for your customer data.",
    },
    [Source.monday]: {
        category: SourceCategory.CRM,
        description: "Connect monday.com as a source for your customer data.",
    },
    [Source.zendesk_sell]: {
        category: SourceCategory.CRM,
        description: "Connect Zendesk Sell as a source for your customer data.",
    },
    [Source.vtiger]: {
        category: SourceCategory.CRM,
        description: "Connect Vtiger CRM as a source for your customer data.",
    },
    [Source.pipe]: {
        category: SourceCategory.CRM,
        description: "Connect Pipedrive as a source for your customer data.",
    },
    [Source.lsqrd]: {
        category: SourceCategory.CRM,
        description: "Connect LeadSquared as a source for your customer data.",
    },
    [Source.b24]: {
        category: SourceCategory.CRM,
        description: "Connect Bitrix24 as a source for your customer data.",
    },
});

export const getSourceCatalogMeta = (code, language) => {
    const meta = SOURCE_CATALOG_META[code] ?? {};
    return {
        category: meta.category ?? SourceCategory.OTHER,
        description: language?.source_catalog?.[code] ?? meta.description ?? "",
    };
};

/**
 * Catalog items for `TypeCatalog` from the raw API source types.
 * @returns {Array<{ code: string, name: string, description: string, category: string, image: string }>}
 */
export const buildSourceCatalogItems = (sourceTypes, language) =>
    (sourceTypes ?? [])
        .filter((item) => item?.code)
        .map((item) => ({
            code: item.code,
            name: item.name ?? item.code,
            image: getSourceIconByCode(item.code),
            testId: `create-source-type-${item.code}`,
            ...getSourceCatalogMeta(item.code, language),
        }));
